import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ClipboardList, AlertTriangle } from "lucide-react";
import confetti from "canvas-confetti";

export default function ActionCheckoff() {
  const { token } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [justDone, setJustDone] = useState(false);

  useEffect(() => {
    api.get(`/public/action-check/${token}`)
      .then(({ data }) => setItem(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has been removed"));
  }, [token]);

  const markDone = async () => {
    setChecking(true);
    try {
      await api.post(`/public/action-check/${token}`);
      setItem((it) => ({ ...it, done: true }));
      setJustDone(true);
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 } });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not update — please try again");
    } finally { setChecking(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const late = item?.due_date && item.due_date < today && !item.done;

  return (
    <div className="min-h-screen bg-secondary/30 flex items-center justify-center p-6" data-testid="action-checkoff-page">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-lg p-8 text-center">
        {error ? (
          <div data-testid="action-checkoff-error">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
            <p className="font-display text-lg font-bold">Link not found</p>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
          </div>
        ) : !item ? (
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        ) : item.done ? (
          <div data-testid="action-checkoff-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <p className="font-display text-2xl font-extrabold tracking-tight">{justDone ? "Nice work!" : "Already completed"}</p>
            <p className="text-sm mt-3 font-medium line-through text-muted-foreground">{item.title}</p>
            <p className="text-xs text-muted-foreground mt-2">{item.project} · marked complete{item.owner ? ` · ${item.owner}` : ""}</p>
            <p className="text-xs text-muted-foreground mt-6">Your project manager has been kept in the loop — nothing else to do.</p>
          </div>
        ) : (
          <div data-testid="action-checkoff-open">
            <ClipboardList className="h-10 w-10 text-primary mx-auto mb-4" />
            <p className="overline text-muted-foreground">Action item · {item.project}</p>
            <p className="font-display text-xl font-extrabold tracking-tight mt-2">{item.title}</p>
            {item.description && <p className="text-sm text-muted-foreground mt-2">{item.description}</p>}
            <p className="text-xs text-muted-foreground mt-3">
              {item.owner && <>Owner: <b>{item.owner}</b> · </>}Due {item.due_date || "no date"}
              {late && <span className="text-red-500 font-bold"> · LATE</span>}
            </p>
            <Button className="w-full mt-6 gap-2 h-11 text-base" onClick={markDone} disabled={checking} data-testid="action-checkoff-button">
              {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Mark complete
            </Button>
            <p className="text-[11px] text-muted-foreground mt-3">One click — no sign-in needed.</p>
          </div>
        )}
      </div>
    </div>
  );
}
